import React from 'react';
import { FileText, AlertCircle, Scale, CheckCircle } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Scale className="w-12 h-12 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
              <p className="text-blue-100 mt-2">Last Updated: November 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and 
              Raven Environmental Consulting, LLC ("Raven Environmental," "we," "us," or "our") concerning 
              your access to and use of our website and services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing or using our website and services, you agree to be bound by these Terms. If you 
              do not agree with any part of these Terms, you must not use our website or services.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm">
                  <strong>Important:</strong> Please read these Terms carefully before using our services. 
                  Your continued use of our services constitutes acceptance of these Terms.
                </p>
              </div>
            </div>
          </section>

          {/* Services Description */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <FileText className="w-6 h-6 text-blue-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Services Provided</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Raven Environmental Consulting, LLC provides environmental consulting services, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Environmental permit application preparation and submission</li>
              <li>Permit feasibility analysis and regulatory assessment</li>
              <li>Equipment selection and compliance guidance</li>
              <li>Pollution control technology evaluation (BACT analysis)</li>
              <li>Compliance monitoring and reporting assistance</li>
              <li>Ongoing regulatory support and consultation</li>
              <li>Air quality permitting for various industrial sectors</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              The specific scope of services will be defined in individual service agreements or proposals.
            </p>
          </section>

          {/* User Obligations */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User Obligations and Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using our website and services, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information when submitting inquiries or forms</li>
              <li>Maintain the confidentiality of any account credentials</li>
              <li>Use our services only for lawful purposes and in accordance with these Terms</li>
              <li>Not engage in any conduct that restricts or inhibits others' use of our services</li>
              <li>Not attempt to gain unauthorized access to our systems or networks</li>
              <li>Not use our services to transmit any harmful, illegal, or malicious content</li>
              <li>Comply with all applicable federal, state, and local laws and regulations</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              All content on our website, including but not limited to text, graphics, logos, images, and software, 
              is the property of Raven Environmental Consulting, LLC or its content suppliers and is protected by 
              United States and international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may not:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Reproduce, distribute, or publicly display our content without written permission</li>
              <li>Modify or create derivative works from our content</li>
              <li>Use our trademarks, service marks, or trade names without authorization</li>
              <li>Remove or alter any copyright, trademark, or proprietary notices</li>
            </ul>
          </section>

          {/* Client Relationship */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Client Relationship and Engagement</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Submission of an inquiry through our website does not establish a client relationship. A formal 
              client relationship is established only when:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>We have mutually agreed upon the scope of services</li>
              <li>A written service agreement or engagement letter has been executed</li>
              <li>Any required retainer or initial payment has been received</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Until a formal engagement is established, any communications or information you provide are not 
              protected by attorney-client privilege or any similar professional confidentiality protections.
            </p>
          </section>

          {/* Professional Standards */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Standards and Limitations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our services are provided in accordance with:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Generally accepted professional standards in environmental consulting</li>
              <li>Applicable federal and Alaska state regulations</li>
              <li>Industry best practices and technical guidelines</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Important Limitations:</strong>
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>We do not guarantee specific outcomes or regulatory approvals</li>
              <li>Regulatory decisions are made by government agencies, not by our firm</li>
              <li>Timelines and processes are subject to agency review and discretion</li>
              <li>Regulatory requirements may change during the course of a project</li>
            </ul>
          </section>

          {/* Disclaimers */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-blue-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Disclaimers and Warranties</h2>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES 
                OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong>
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, we disclaim all warranties, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Warranties of merchantability and fitness for a particular purpose</li>
                <li>Warranties regarding accuracy, reliability, or completeness of content</li>
                <li>Warranties that our services will be uninterrupted, secure, or error-free</li>
                <li>Warranties regarding specific results or outcomes</li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Information on our website is for general informational purposes only and should not be construed 
              as professional advice. Specific advice requires evaluation of individual circumstances through 
              a formal engagement.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <p className="text-gray-700 leading-relaxed font-semibold mb-2">
                IMPORTANT LEGAL NOTICE
              </p>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RAVEN ENVIRONMENTAL CONSULTING, LLC 
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF 
                DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our total liability to you for any claims arising out of or related to these Terms or our services 
              shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          {/* Indemnification */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless Raven Environmental Consulting, LLC and its 
              officers, employees, contractors, and agents from and against any claims, liabilities, damages, 
              losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Your use or misuse of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or regulation</li>
              <li>Your violation of any rights of another party</li>
              <li>Any information or content you submit through our website</li>
            </ul>
          </section>

          {/* Payment Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For professional services provided under a formal engagement:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Fees and payment terms will be specified in your service agreement</li>
              <li>Payment is due according to the terms specified in your invoice</li>
              <li>Late payments may be subject to interest charges as permitted by law</li>
              <li>We reserve the right to suspend services for non-payment</li>
              <li>You are responsible for all costs of collection, including attorneys' fees</li>
            </ul>
          </section>

          {/* Confidentiality */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Confidentiality</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For clients with formal service agreements, we maintain confidentiality of your project information 
              in accordance with:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>The terms of your specific service agreement</li>
              <li>Our Privacy Policy</li>
              <li>Applicable professional standards and regulations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              However, we may disclose confidential information when required by law, regulation, or 
              regulatory authority.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
              <li>Refuse service to anyone for any reason at any time</li>
              <li>Terminate or suspend your access to our website without notice</li>
              <li>Remove any content you submit that violates these Terms</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Upon termination, all provisions of these Terms that by their nature should survive shall survive, 
              including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Dispute Resolution and Governing Law</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance 
              with the laws of the State of Alaska, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Jurisdiction:</strong> You agree that any legal action or proceeding arising out of 
              or related to these Terms shall be brought exclusively in the state or federal courts located 
              in Alaska, and you consent to the jurisdiction of such courts.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Informal Resolution:</strong> Before filing any formal claim, you agree to attempt to 
              resolve the dispute informally by contacting us. We will attempt to resolve disputes in good faith.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. 
              If a revision is material, we will provide at least 30 days' notice prior to any new terms 
              taking effect.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of our services after any changes to these Terms constitutes acceptance 
              of those changes. We encourage you to review these Terms periodically.
            </p>
          </section>

          {/* Severability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall 
              be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise 
              remain in full force and effect.
            </p>
          </section>

          {/* Entire Agreement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Entire Agreement</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms, together with our Privacy Policy and any service agreements, constitute the entire 
              agreement between you and Raven Environmental Consulting, LLC regarding your use of our website 
              and services, and supersede all prior communications and proposals.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 text-blue-700 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Contact Information</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions or concerns about these Terms of Service, please contact us:
            </p>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Raven Environmental Consulting, LLC</p>
              <p className="text-gray-700 mb-1">Email: info@raven-environmental.com</p>
              <p className="text-gray-700 mb-1">Phone: (907) 687-8263</p>
              <p className="text-gray-700">Located in Fairbanks, Alaska</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section>
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Acknowledgment</h3>
              <p className="text-gray-700 leading-relaxed">
                BY USING OUR WEBSITE AND SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE 
                TO BE BOUND BY THESE TERMS OF SERVICE. IF YOU DO NOT AGREE TO THESE TERMS, PLEASE DO NOT USE 
                OUR WEBSITE OR SERVICES.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
